import { useTranslation } from 'next-i18next'
// Dependencies.
import styled, { css } from 'styled-components'
import { ContentState, Editor, EditorState } from 'draft-js'
import { useState, useEffect, useRef } from 'react'

import { EventHookInterface } from '../../../types'
import { FormField } from '../../Inputs/FormField'
import {
  forScreensGreaterThanMobileWidth,
  colors,
  mobileScreen,
  fontWeights,
} from '../../../utils'
import { P } from '../../P'

// Div.
const Div = styled.div`
  position: relative;
  max-width: 100%;
  min-height: 54px;
  text-align: left;
  width: 472px;

  .DraftEditor-root {
    /* height: 100%; */
    max-width: 100%;
    overflow: hidden;
  }

  /* Placeholder. */
  .DraftEditor-root * {
    font-weight: ${fontWeights.bold};
    font-size: 36px;
    line-height: 110%;
    letter-spacing: -0.8px;

    ${mobileScreen(css`
      font-size: 27px;

      letter-spacing: -0.5px;
    `)}
  }

  .public-DraftEditorPlaceholder-inner {
    color: #a6a6a6;
    max-height: 100%;
    overflow: hidden;
    pointer-events: none;
    position: absolute;
    text-overflow: ellipsis;
    width: 100%;
  }

  &.readyOnly .public-DraftEditorPlaceholder-inner {
    display: none;
  }

  .errorContainer {
    margin-top: 8px;
  }

  &.inputError {
    & .DraftEditor-root {
      border: 2px solid ${colors.red};
      border-radius: 6px;
      opacity: 0.6;
    }
  }

  /* Desktop. */
  ${forScreensGreaterThanMobileWidth(css`
    &.readOnly {
      margin-bottom: 6px;
    }
  `)}
`

// Props.
interface Props {
  eventHook: EventHookInterface
}

// Text Area.
export const TitleField: React.FC<Props> = (props) => {
  const { t } = useTranslation('common')
  // Props.
  const { eventHook } = props
  const { isEditable } = eventHook
  const { title, id } = eventHook.value

  // Refs.
  const editorRef = useRef<Editor>(null)

  // State.
  const [editorState, setEditorState] = useState<EditorState>()
  const [doneFirstTimeFocus, setDoneFirstTimeFocus] = useState(false)

  // On Change — Value.
  useEffect(() => {
    const editorStateDoesNotExist = !editorState
    const valueIsOriginal =
      editorState && title !== editorState.getCurrentContent().getPlainText()
    const shouldUpdateEditorState = editorStateDoesNotExist || valueIsOriginal
    const value = isEditable ? title : title || '-'
    if (shouldUpdateEditorState)
      setEditorState(
        EditorState.createWithContent(ContentState.createFromText(value || '')),
      )
  }, [isEditable, title])

  useEffect(() => {
    if (
      !id &&
      isEditable &&
      editorRef.current &&
      !doneFirstTimeFocus &&
      !title
    ) {
      editorRef.current.focus()
      setDoneFirstTimeFocus(true)
    }
  }, [editorRef.current, doneFirstTimeFocus, id, title])

  // On Change.
  const onChange = (editorState: EditorState) => {
    // Update Local State.
    setEditorState(editorState)

    // Trigger `props.onChange`.
    const currentText = editorState.getCurrentContent().getPlainText()
    if ((currentText && currentText.length > 0) || title)
      eventHook.updateFieldsWithPartial({ title: currentText })
  }

  // Title Input.
  const TitleInput = () =>
    (editorState && (
      <Div
        className={`textArea titleField ${(!isEditable && 'readOnly') || ''} ${
          (eventHook.error.title &&
            eventHook.error.title.length > 0 &&
            'inputError') ||
          ''
        } titleInput`}>
        <Editor
          ref={editorRef}
          editorKey="eventTitle"
          editorState={editorState}
          onChange={onChange}
          placeholder={t(`Add event title`, `Add event title`)}
          readOnly={!isEditable}
          spellCheck
        />
        {eventHook.error.title && eventHook.error.title.length > 0 && (
          <div className="errorContainer">
            {eventHook.error.title.map((error, index) => {
              return (
                <P
                  key={index}
                  className="detail"
                  style={{ color: `${colors.red}` }}>
                  {error}
                </P>
              )
            })}
          </div>
        )}
      </Div>
    )) ||
    null

  // Read Only State (Sans Field Layer).
  if (!isEditable) return <TitleInput />

  // Editable State.
  return (
    <FormField
      className="titleField"
      required
      title={t(`Event details`, `Event details`)}>
      {/* <TitleInput /> */}
      {editorState && (
        <Div
          className={`${
            (eventHook.error.title &&
              eventHook.error.title.length > 0 &&
              'inputError') ||
            ''
          } titleInput`}>
          <Editor
            ref={editorRef}
            editorKey="eventTitle"
            editorState={editorState}
            onChange={onChange}
            placeholder={t(`Add event title`, `Add event title`)}
            readOnly={!isEditable}
            spellCheck
          />
          {eventHook.error.title && eventHook.error.title.length > 0 && (
            <div className="errorContainer">
              {eventHook.error.title.map((error, index) => {
                return (
                  <P
                    key={index}
                    className="detail"
                    style={{ color: `${colors.red}` }}>
                    {error}
                  </P>
                )
              })}
            </div>
          )}
        </Div>
      )}
    </FormField>
  )
}
